import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ToastContainer, toast } from 'react-toastify';
import cookies from 'react-cookies';
import { ToastWrapper, ToastBody, WishListContent, LinkWrap, LinkButton } from './styles';

export const callToast = toast;

export const cartIcon = () => (
    <svg
        viewBox="0 0 1024 1024"
        focusable="false"
        className=""
        data-icon="shopping-cart"
        width="20px"
        height="20px"
        fill="currentColor"
        aria-hidden="true"
    >
        <path d="M922.9 701.9H327.4l29.9-60.9 496.8-.9c16.8 0 31.2-12 34.2-28.6l68.8-385.1c1.8-10.1-.9-20.5-7.5-28.4a34.99 34.99 0 0 0-26.6-12.5l-632-2.1-5.4-25.4c-3.4-16.2-18-28-34.6-28H96.5a35.3 35.3 0 1 0 0 70.6h125.9L246 312.8l58.1 281.3-74.8 122.1a34.96 34.96 0 0 0-3 36.8c6 11.9 18.1 19.4 31.5 19.4h62.8a102.43 102.43 0 0 0-20.6 61.7c0 56.6 46 102.6 102.6 102.6s102.6-46 102.6-102.6c0-22.3-7.4-44-20.6-61.7h161.1a102.43 102.43 0 0 0-20.6 61.7c0 56.6 46 102.6 102.6 102.6s102.6-46 102.6-102.6c0-22.3-7.4-44-20.6-61.7H923c19.4 0 35.3-15.8 35.3-35.3a35.42 35.42 0 0 0-35.4-35.2zM305.7 253l575.8 1.9-56.4 315.8-452.3.8L305.7 253zm96.9 612.7c-17.4 0-31.6-14.2-31.6-31.6 0-17.4 14.2-31.6 31.6-31.6s31.6 14.2 31.6 31.6a31.6 31.6 0 0 1-31.6 31.6zm325.1 0c-17.4 0-31.6-14.2-31.6-31.6 0-17.4 14.2-31.6 31.6-31.6s31.6 14.2 31.6 31.6a31.6 31.6 0 0 1-31.6 31.6z" />
    </svg>
);

const FaIcons = {
    tag: {
        name: 'faTag',
        loadable: loadable.lib(() => import('@fortawesome/free-solid-svg-icons/faTag')),
    },
    ['shopping-cart']: {
        name: 'shopping-cart',
        loadable: cartIcon,
    },
};

export const ToastContent = (props) => {
    const LoadableIcon = FaIcons[props.icon] || FaIcons.tag;

    // Temporary notification, to add the necessary icons
    useEffect(() => {
        if (!FaIcons[props.icon]) {
            console.log(`Attempt to use an unknown icon '${props.icon}'`);
        }
    }, [props.icon]);

    const { header, text, link, button } = props;

    return (
        <ToastBody>
            <span>
                <React.Fragment>
                    {LoadableIcon && (
                        <LoadableIcon.loadable
                            fallback={<FontAwesomeIcon icon={faCheck} fixedWidth />}
                        >
                            {(res) => <FontAwesomeIcon icon={res[LoadableIcon.name]} fixedWidth />}
                        </LoadableIcon.loadable>
                    )}
                </React.Fragment>
                <React.Fragment>{header}</React.Fragment>
            </span>
            <React.Fragment>{text}</React.Fragment>
            {link && button && (
                <LinkWrap>
                    <LinkButton to={link}>{button}</LinkButton>
                </LinkWrap>
            )}
        </ToastBody>
    );
};

ToastContent.propTypes = {
    header: PropTypes.string,
    text: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    link: PropTypes.string,
    button: PropTypes.string,
};

ToastContent.defaultProps = {
    icon: 'tag',
};

export const WishListToast = (props) => (
    <WishListContent>
        <svg width="40" height="40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M19.96 0C8.938 0 0 8.937 0 19.96c0 11.025 8.937 19.962 19.96 19.962 11.025 0 19.962-8.937 19.962-19.961S30.985 0 19.96 0zm.728 30.365s-.39.38-.74.38-.721-.388-.721-.388c-5.558-5.207-10.91-8.376-10.91-13.584 0-3.908 2.911-6.793 5.822-6.793 4.852 0 5.822 2.911 5.822 2.911s.97-2.91 5.82-2.91c2.913 0 5.824 2.923 5.824 6.792 0 5.216-5.348 8.375-10.917 13.592z"
                fill="#00B357"
            />
        </svg>
        <div>{props.text}</div>
    </WishListContent>
);

WishListToast.propTypes = {
    text: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};

export const ToastWrap = (props) => {
    React.useEffect(() => {
        const isNotFirstDisplay = cookies.load('ts_terms');
        if (isNotFirstDisplay) {
            return;
        }
        callToast(
            <ToastContent
                header="This site uses cookies"
                text={
                    <>
                        This website uses cookies. Please visit our{' '}
                        <a
                            href="https://www.tripshock.com/policy/"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            Privacy Policy
                        </a>
                        &nbsp; to learn how we use cookies and how to control your cookie settings.
                        By clicking or navigating this site you consent to our use of cookies.
                    </>
                }
            />
        );
        cookies.save('ts_terms', true, {
            expiration: 2147483647,
        });
    }, []);
    return (
        <ToastWrapper>
            <ToastContainer position="top-left" autoClose={3000} {...props} />
        </ToastWrapper>
    );
};
